import { Location } from "@angular/common";
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
} from "@angular/core";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatStepper } from "@angular/material/stepper";
import { ActivatedRoute } from "@angular/router";
import moment from "moment";
import { Subscription, of } from "rxjs";
import { catchError, map } from "rxjs/operators";

import {
	formatRecommendationClearance,
	formatRecommendationObjective,
	formatRecommendationYesNo,
	isRecommendationFormType,
	isRecommendationTraining,
	recommendationSessionLabel,
} from "src/app/cbta-web/ratings/container-ratings-form/shared-v2/utils/recommendation-summary.utils";
import { normalizeCandidateSessionConfigurations } from "src/app/cbta-web/ratings/container-ratings-form/shared-v2/utils/session-form.utils";
import { CONSTANT } from "src/app/constant";
import { RouteType } from "src/app/models/enum";
import { ManeuverBreakdownItem, Observation, ReportSummary, ReportSummaryComment } from "src/app/models/models";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { buildSummaryReportTitle } from "src/app/shared/utils/form-type-label";
import { getRankLabel } from "src/app/shared/utils/rank-label.util";
import { HelperService } from "src/app/util/helper.service";
import { formatReportSummarySignatureLocal } from "./report-summary-signature-time";
import {
	isPfPmReportLayout,
	ReportSummaryLayoutContext,
	resolveReportSummaryLayout,
} from "./resolvers";
import { Ts1PfpmReportLayoutComponent } from "./layouts/ts1-pfpm-report-layout/ts1-pfpm-report-layout.component";

@Component({
	selector: "app-report-summary",
	templateUrl: "./report-summary.component.html",
	styleUrls: ["./report-summary.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ReportSummaryComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() checklistMode = false;
	@Input() initialStepIndex = 0;
	@Input() embeddedCandidateId?: string;
	@Input() set embeddedReportSummary(value: ReportSummary | null | undefined) {
		this._embeddedReportSummary = value ?? undefined;
	}
	@Output() checklistBack = new EventEmitter<void>();
	@Output() checklistNext = new EventEmitter<void>();

	@ViewChild("summaryReport") summaryReport!: ElementRef;
	@ViewChild("instructorNotes") instructorNotes!: ElementRef;
	@ViewChild("reportContentsRef") reportContentsRef!: ElementRef;
	@ViewChild("maneuverBreakdownRef") maneuverBreakdownRef!: ElementRef;
	@ViewChild("stepper") stepper?: MatStepper;
	@ViewChild(Ts1PfpmReportLayoutComponent) pfpmLayout?: Ts1PfpmReportLayoutComponent;

	constructor(
		private route: ActivatedRoute,
		private httpService: HttpService,
		public sharedService: SharedService,
		private loaderService: LoaderService,
		private location: Location,
		private fb: UntypedFormBuilder,
		public roleService: RoleService,
		private cdr: ChangeDetectorRef,
		public helperService: HelperService,
	) {}

	// ===== Flags / State =====
	isDownloading = false;

	isTS1 = false;
	isTS6 = false;
	isTS3 = false;
	isTS5 = false;
	isCC = false;

	layoutContext: ReportSummaryLayoutContext = { layoutKey: "legacy", formType: "" };

	comments: ReportSummaryComment[] = [];
	otherComments: string[] = [];
	sessionList: string[] = [];

	candidateId?: string;

	routeSub!: Subscription;
	summarySub!: Subscription;

	private _reportSummary?: ReportSummary;
	private _embeddedReportSummary?: ReportSummary;

	// ===== Maneuver & OBS (aligned with QuickRatingSummaryDetails) =====
	createNewForm!: UntypedFormGroup;
	get maneuvers(): UntypedFormArray {
		return this.createNewForm.get("maneuvers") as UntypedFormArray;
	}

	fixedManeuvers: any[] = []; // for non-TS1 accordion headers
	meneuverNameList: string[] = []; // kept for potential headers
	obAddRemoveList: any[] = []; // not used by app-maneuver-form but kept for parity

	observationForm!: UntypedFormGroup; // not used in template now, retained for compatibility

	ngOnInit(): void {
		if (!this.checklistMode) {
			this.sharedService.setActivePage(RouteType.REPORT);
		}
		this.initCreateNewForm();
		this.initObservationsForm();
		if (this.checklistMode) {
			this.applyEmbeddedReportSummary();
		} else {
			this.initSummary();
		}
	}

	ngAfterViewInit(): void {
		this.restoreChecklistStepIndex();
	}

	ngOnDestroy(): void {
		if (this.routeSub) this.routeSub.unsubscribe();
		if (this.summarySub) this.summarySub.unsubscribe();
		this.loaderService.setLoading(false);
	}

	// ====== Fetch / Init ======
	initSummary(): void {
		this.loaderService.setLoading(true);
		this.routeSub = this.route.params.subscribe(
			(params) => {
				this.candidateId = params["candidateId"];
				if (!this.candidateId) {
					this.loaderService.setLoading(false);
					return;
				}

				const role = this.roleService.getUserRole();
				this.summarySub = this.httpService
					.getSummary(this.candidateId, { role })
					.pipe(
						map((resp: any) => resp?.data as ReportSummary),
						catchError(() => of(undefined as unknown as ReportSummary)),
					)
					.subscribe(
						(reportSummary) => {
							try {
								if (reportSummary) {
									this.setReportSummary(reportSummary);
									this.rebuildManeuversFromSummary();
								}
							} finally {
								this.loaderService.setLoading(false);
								this.cdr.detectChanges();
							}
						},
						() => this.loaderService.setLoading(false),
					);
			},
			() => this.loaderService.setLoading(false),
		);
	}

	private applyEmbeddedReportSummary(): void {
		const reportSummary = this._embeddedReportSummary;
		if (!reportSummary) return;

		this.candidateId = this.embeddedCandidateId;
		this.setReportSummary(reportSummary);
		this.rebuildManeuversFromSummary();
		this.cdr.detectChanges();
	}

	setReportSummary(reportSummary: ReportSummary) {
		this._reportSummary = reportSummary;
		this.layoutContext = resolveReportSummaryLayout(reportSummary);

		// form type flags (legacy mat-stepper branch only)
		const ft = (reportSummary as any)?.formType;

		this.isTS6 = ft === CONSTANT.FORM_TYPE.ts_6;
		this.isTS1 = ft === CONSTANT.FORM_TYPE.ts_1;
		this.isTS3 = ft === CONSTANT.FORM_TYPE.ts_3;
		this.isTS5 = ft === CONSTANT.FORM_TYPE.ts_5;
		this.isCC = ft === CONSTANT.FORM_TYPE.cabin_crew || ft === CONSTANT.FORM_TYPE.cabin_crew_instructor;

		// top sections
		this.comments = reportSummary?.trainerExaminerNotes?.summary ?? [];
		this.otherComments = reportSummary?.trainerExaminerNotes?.otherTrainingAndObservations ?? [];
		this.sessionList = reportSummary?.sessionDetails?.sessionDetails ?? [];
	}

	get canDownloadSummary(): boolean {
		return true;
	}

	get reportSummary(): ReportSummary | undefined {
		return this._reportSummary;
	}

	get summaryReportTitle(): string {
		return buildSummaryReportTitle(this._reportSummary?.formType);
	}

	// ====== UI Helpers ======
	sessionConfigurations(summary?: ReportSummary) {
		return normalizeCandidateSessionConfigurations(summary?.candidateDetails?.candidateSessionConfigurations);
	}

	join(arr?: string[]) {
		return arr ? arr.join(", ") : "";
	}

	getRank(rank?: string) {
		return getRankLabel(rank);
	}

	get isPfPmLayout(): boolean {
		return isPfPmReportLayout(this.layoutContext.layoutKey);
	}

	get pfPmLayoutKey(): "ts1-pfpm-v3" | "ts1-pfpm-v4" {
		const key = this.layoutContext.layoutKey;
		if (key === "ts1-pfpm-v3" || key === "ts1-pfpm-v4") {
			return key;
		}
		return "ts1-pfpm-v3";
	}

	getSelectedStepIndex(): number {
		if (this.isPfPmLayout && this.pfpmLayout) {
			return this.pfpmLayout.pageIndex;
		}
		return this.stepper?.selectedIndex ?? 0;
	}

	goBack() {
		if (this.checklistMode) {
			const currentIndex = this.getSelectedStepIndex();
			if (currentIndex <= 0) {
				this.checklistBack.emit();
				return;
			}
			this.goToStepIndex(currentIndex - 1);
			return;
		}
		if (this.isPfPmLayout && this.pfpmLayout) {
			if (this.pfpmLayout.pageIndex > 0) {
				this.pfpmLayout.pageIndex = 0;
				this.scrollReportToTop();
				return;
			}
			this.location.back();
			return;
		}
		if (!this.stepper) {
			this.location.back();
			return;
		}
		this.gotoTop("back");
	}

	goNext(): void {
		if (this.checklistMode && this.isLastStep) {
			this.checklistNext.emit();
			return;
		}
		if (this.isPfPmLayout && this.pfpmLayout?.showNextButton) {
			this.pfpmLayout.pageIndex = 1;
			this.scrollReportToTop();
			return;
		}
		this.gotoTop("next");
	}

	private scrollReportToTop(): void {
		setTimeout(() => {
			const element = document.getElementById("cbta-web-page-container") as HTMLElement;
			if (element) element.scrollTop = 0;
		}, 100);
	}

	private restoreChecklistStepIndex(): void {
		if (!this.checklistMode || this.initialStepIndex <= 0) return;
		setTimeout(() => this.goToStepIndex(this.initialStepIndex, false));
	}

	private goToStepIndex(index: number, scroll = true): void {
		if (scroll) {
			this.scrollReportToTop();
		}

		if (this.isPfPmLayout && this.pfpmLayout) {
			const maxIndex = this.pfpmLayout.hasManeuverBreakdown ? 1 : 0;
			this.pfpmLayout.pageIndex = Math.min(Math.max(index, 0), maxIndex);
			this.cdr.detectChanges();
			return;
		}

		if (!this.stepper) return;

		const maxIndex = Math.max(0, this.getTotalStepCount() - 1);
		this.stepper.selectedIndex = Math.min(Math.max(index, 0), maxIndex);
		this.cdr.detectChanges();
	}

	gotoTop(direction: "next" | "back") {
		setTimeout(() => {
			const element = document.getElementById("cbta-web-page-container") as HTMLElement;
			if (element) element.scrollTop = 0;

			if (!this.stepper) return;
			if (direction === "next") {
				this.stepper.next();
			} else if (direction === "back") {
				if (this.stepper.selectedIndex === 0) {
					if (this.checklistMode) {
						this.checklistBack.emit();
					} else {
						this.location.back();
					}
				} else {
					this.stepper.previous();
				}
			}
		}, 100);
	}

	formatSignatureTime(dateString?: string): string {
		return formatReportSummarySignatureLocal(dateString);
	}

	formatSummaryDate(dateString?: string): string {
		if (!dateString) {
			return "-";
		}

		const parsed = moment(dateString);
		return parsed.isValid() ? parsed.format(CONSTANT.DATE_FORMAT.DISPLAY_DATE_FORMAT) : "-";
	}

	readonly isRecommendationFormType = isRecommendationFormType;
	readonly isRecommendationTraining = isRecommendationTraining;
	readonly recommendationSessionLabel = recommendationSessionLabel;
	readonly formatRecommendationYesNo = formatRecommendationYesNo;
	readonly formatRecommendationObjective = formatRecommendationObjective;
	readonly formatRecommendationClearance = formatRecommendationClearance;

	get logo() {
		return this.reportSummary?.logo;
	}

	get instructorSignature(): string {
		return this.resolveSignatureImage(this.reportSummary?.sessionDetails?.instructorSignature);
	}

	get candidateSignature(): string {
		return this.resolveSignatureImage(this.reportSummary?.sessionDetails?.traineeSignature);
	}

	private resolveSignatureImage(signature: unknown): string {
		if (!signature) return "";

		if (typeof signature === "string") {
			if (signature.startsWith("data:")) return signature;
			return `data:image/png;base64,${signature}`;
		}

		if (ArrayBuffer.isView(signature)) {
			return this.helperService.bufferDataToImageUrl(Array.from(new Uint8Array(signature.buffer)));
		}

		const bufferData = (signature as { data?: number[] })?.data;
		return this.helperService.bufferDataToImageUrl(bufferData);
	}

	// ====== Download (kept behavior) ======
	downloadSummary() {
		this.loaderService.setLoading(true);

		const pdfUrl = this.reportSummary?.report_file_signed_url;
		// TS1 v3 and CC/CCI PDF layouts are rendered server-side; always regenerate so downloads
		// reflect the current template instead of a previously stored report file.
		if (pdfUrl && !this.isPfPmLayout && !this.isCC) {
			this.sharedService.download(pdfUrl);
			this.loaderService.setLoading(false);
			return;
		}

		if (!this.candidateId) {
			this.loaderService.setLoading(false);
			return;
		}

		this.httpService
			.generateSummary(this.candidateId, {
				downloadOnly: true,
				role: this.roleService.getUserRole(),
			})
			.subscribe(
				(result) => {
					const signedUrl = result?.data?.report_file_signed_url;
					if (signedUrl) this.sharedService.download(signedUrl);
					this.loaderService.setLoading(false);
				},
				(error) => {
					console.error(error);
					this.loaderService.setLoading(false);
				},
			);
	}

	// ====== Form Builders (same shape as QuickRatingSummaryDetails) ======
	private initCreateNewForm(): void {
		this.createNewForm = this.fb.group({
			formType: ["", [Validators.required]],
			formName: ["", [Validators.required]],
			syllabusStartDate: [new Date(), [Validators.required]],
			syllabusEndDate: [new Date(), [Validators.required]],
			sessions: this.fb.group({}),
			maneuvers: this.fb.array([]),
		});

		// we manage maneuvers programmatically
		this.maneuvers.clearValidators();
		this.maneuvers.updateValueAndValidity({ emitEvent: false, onlySelf: true });
	}

	initObservationsForm(): void {
		this.observationForm = this.fb.group({
			observations: this.fb.array([]),
		});
	}

	private makeObsValue() {
		return {
			totalCount: 0,
			currentCount: 0,
			preDefined: true,
			lastAction: CONSTANT.OB_LAST_ACTION.CREATE,
			isDisabled: false,
			showButton: false,
		};
	}

	/** CC/CCI: allow multi-segment categories like CC_COM_1 (same TS-1-only validator rule) */
	private buildManeuverCrewFromObservations(observations: Observation[], formType: string): UntypedFormGroup {
		const byCategory = new Map<string, Array<{ key: string; num: number }>>();
		const seen = new Set<string>();

		for (const o of observations || []) {
			const code = (o.code || "").trim();
			if (!code || seen.has(code)) continue;
			seen.add(code);

			const m = /^([a-zA-Z]+(?:_[a-zA-Z]+)*)_(\d+)$/.exec(code);
			if (!m) continue;
			const cat = m[1].toUpperCase();
			const num = Number(m[2]);
			const key = `${cat}${num}`;
			if (!byCategory.has(cat)) byCategory.set(cat, []);
			byCategory.get(cat)!.push({ key, num });
		}

		const categoryGroups: Record<string, any> = {};
		for (const [cat, items] of byCategory.entries()) {
			items.sort((a, b) => a.num - b.num);
			const controls: Record<string, any> = {};
			for (const it of items) controls[it.key] = [{ value: this.makeObsValue(), disabled: false }];
			categoryGroups[cat] = this.fb.group(controls);
		}

		const group = this.fb.group({
			maneuver: ["", [Validators.required]],
			formType: [formType],
			maneuverCode: [""],
			assessmentId: [""],
			traineeAssessmentId: [""],
			candidateId: [""],
			selectedOBs: [[]],
			safetyOutcome: [-2],
			repeat: [0],
			...categoryGroups,
		});

		return group;
	}

	private buildManeuverFromObservations(observations: Observation[], formType: string): UntypedFormGroup {
		const byCategory = new Map<string, Array<{ key: string; num: number }>>();
		const seen = new Set<string>();

		for (const o of observations || []) {
			const code = (o.code || "").trim();
			if (!code || seen.has(code)) continue;
			seen.add(code);

			const m = /^([a-zA-Z]+)_(\d+)$/.exec(code);
			if (!m) continue;
			const cat = m[1].toUpperCase();
			const num = Number(m[2]);
			const key = `${cat}${num}`;
			if (!byCategory.has(cat)) byCategory.set(cat, []);
			byCategory.get(cat)!.push({ key, num });
		}

		const categoryGroups: Record<string, any> = {};
		for (const [cat, items] of byCategory.entries()) {
			items.sort((a, b) => a.num - b.num);
			const controls: Record<string, any> = {};
			for (const it of items) controls[it.key] = [{ value: this.makeObsValue(), disabled: false }];
			categoryGroups[cat] = this.fb.group(controls);
		}

		const group = this.fb.group({
			maneuver: ["", []],
			formType: [formType],
			maneuverCode: [""],
			assessmentId: [""],
			traineeAssessmentId: [""],
			candidateId: [""],
			selectedOBs: [[]],
			safetyOutcome: [-2],
			repeat: [0],
			...categoryGroups,
		});

		return group;
	}

	/** Set maneuver name/code on group header */
	private setManeuverOnGroup(mfg: UntypedFormGroup, opt: { code: string; description?: string }) {
		mfg.get("maneuver")?.setValue({ code: opt.code, description: opt.description ?? "" });
		mfg.get("maneuver")?.markAsDirty();
		mfg.get("maneuver")?.updateValueAndValidity({ emitEvent: false });
	}

	/** Read master data & patch with reportSummary.maneuverBreakdown just like QuickRatingSummaryDetails */
	private rebuildManeuversFromSummary(): void {
		if (this.isPfPmLayout) {
			return;
		}

		this.meneuverNameList = [];
		this.obAddRemoveList = [];
		this.fixedManeuvers = [];
		this.maneuvers.clear();

		const reportSummary: any = this._reportSummary;
		if (!reportSummary) return;

		// flags already set in setReportSummary
		const formType = reportSummary?.formType;
		const masterData = this.sharedService.formMasterData?.[formType];
		const maneuvers = masterData?.maneuvers;

		if (this.isTS1) {
			Object.keys(reportSummary?.maneuverBreakdown ?? {}).forEach((maneuverCode) => {
				// TS-1: a single maneuver placeholder built from (currently) empty observations list
				const observations: Observation[] = masterData?.observations;
				const mfg = this.buildManeuverFromObservations(observations, formType);

				const maneuverName = this.sharedService.dropDownOptionsManeuver.find((code) => {
					return code.code == maneuverCode;
				});
				mfg.get("maneuver")?.patchValue(maneuverName);
				this.maneuvers.push(mfg);
			});
		} else if (this.isCC) {
			console.log("reportSummary?.formType", reportSummary?.formType);
			maneuvers.forEach((m: any) => {
				const obsForM = masterData?.observations.filter((o: any) => o.fixed_maneuver === m.code);
				const mfg = this.buildManeuverCrewFromObservations(obsForM, reportSummary?.formType);
				this.maneuvers.push(mfg);
				this.fixedManeuvers.push(m);
				this.setManeuverOnGroup(mfg, { code: m.code, description: m.description });
			});
		} else {
			// non-TS1: build a group per fixed maneuver from master data
			maneuvers?.forEach((m: any) => {
				const observationForManeuver = masterData?.observations.filter((o: any) => o.fixed_maneuver === m.code);
				const mfg = this.buildManeuverFromObservations(observationForManeuver, formType);
				this.maneuvers.push(mfg);
				this.fixedManeuvers.push(m);
				console.log("m", m);

				if (this.isTS6) {
					// ETS-6 uses the form type as the "maneuver code" label
					this.setManeuverOnGroup(mfg, { code: formType, description: "" });
				} else {
					this.setManeuverOnGroup(mfg, { code: m.code, description: m.description });
				}
			});
		}

		// Patch values from maneuverBreakdown (attempt counts, lastAction), index-aligned
		const breakdown = reportSummary?.maneuverBreakdown;
		if (breakdown) {
			Object.keys(breakdown).forEach((observationKey, i) => {
				const observationValue = breakdown[observationKey];
				const mfg = this.maneuvers.at(i) as UntypedFormGroup;
				if (!mfg || !observationValue) return;

				Object.keys(observationValue).forEach((code: string) => {
					let obCtrl = null;
					if (!this.isCC) {
						const [cat, num] = String(code).split("_", 2);
						const groupKey = (cat || "").toUpperCase();
						const obKey = `${groupKey}${num}`;

						const mfg = this.maneuvers.at(i) as UntypedFormGroup;
						const catGroup = mfg.get(groupKey) as UntypedFormGroup | null;
						console.log("catGroup", catGroup);
						console.log("groupKey", groupKey);
						if (!catGroup) return;

						obCtrl = catGroup.get(obKey) as any;
						if (!obCtrl) return;
					} else {
						const [prefix, cat, num] = String(code).split("_", 3);
						const groupKey = (prefix + "_" + cat || "").toUpperCase();
						const obKey = `${groupKey}${num}`;

						const mfg = this.maneuvers.at(i) as UntypedFormGroup;
						const catGroup = mfg.get(groupKey) as UntypedFormGroup | null;
						console.log("catGroup", catGroup);
						console.log("groupKey", groupKey);
						if (!catGroup) return;

						obCtrl = catGroup.get(obKey) as any;
						if (!obCtrl) return;
					}

					const payload = (observationValue as any)[code] ?? {};
					const currentCount = payload.activeAttempt ?? payload.attempt ?? 2;
					const totalCount = payload.totalAttempt ?? payload.attempt ?? 2;

					const lastAction =
						payload?.isRemoved === true
							? CONSTANT.OB_LAST_ACTION.REMOVED
							: payload?.isAdded === true
								? CONSTANT.OB_LAST_ACTION.ADDED
								: CONSTANT.OB_LAST_ACTION.NO_ACTION;

					const prev = (obCtrl.getRawValue?.() ?? obCtrl.value) || {};
					const next = {
						...prev,
						currentCount,
						totalCount,
						lastAction,
						isRemoved: payload.isRemoved,
						preDefined: payload.isOriginal,
						isDisabled: payload.isDisabled,
					};
					obCtrl.patchValue(next, { emitEvent: false });
				});

				mfg.updateValueAndValidity({ emitEvent: false });
			});

			this.createNewForm.updateValueAndValidity({ emitEvent: false });
		}

		console.log("this.maneuver", this.maneuvers);
		this.cdr.detectChanges();
	}

	// ===== Accessors kept for compatibility =====
	get maneuverValues(): ManeuverBreakdownItem[] {
		return this.reportSummary?.maneuverBreakdown ? Object.values(this.reportSummary?.maneuverBreakdown) : [];
	}

	showExpansion(maneuver: any): boolean {
		if (!maneuver) return false;

		const v = (maneuver as any)?.getRawValue();
		if (!v || typeof v !== "object") return false;

		const baseFields = new Set([
			"maneuver",
			"maneuverCode",
			"formType",
			"assessmentId",
			"traineeAssessmentId",
			"candidateId",
			"selectedOBs",
			"safetyOutcome",
			"repeat",
		]);

		// remove base keys; check remaining count
		const remainingKeys = Object.keys(v).filter((k) => !baseFields.has(k));

		return remainingKeys.length > 1; // "more than one" → true, else false
	}

	getObValues(code: string) {
		if (!this.reportSummary?.maneuverBreakdown) return [];
		return this.reportSummary?.maneuverBreakdown[code]
			? Object.values(this.reportSummary?.maneuverBreakdown[code])
			: [];
	}

	getTotalStepCount(): number {
		// Default: 2 steps (Summary + Maneuver Breakdown)
		// If TS1, TS3, or CC: 3 steps (Summary, Performance Metrics, Maneuver Breakdown)
		if (this.isTS1 || this.isTS3 || this.isCC) {
			return 3;
		}
		return 2;
	}

	get isLastStep(): boolean {
		if (this.checklistMode && this.isPfPmLayout && this.pfpmLayout) {
			return !this.pfpmLayout.showNextButton;
		}
		return this.stepper ? this.stepper.selectedIndex === this.getTotalStepCount() - 1 : false;
	}
}
